/**
 * NotificationBell Component — Xyden Leadership Assessment Platform
 * Step 6F Green Phase
 */

import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import type { Notification } from '../../types';

export interface NotificationBellProps {
  notifications: Notification[];
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  error?: string;
  /** Optional render prop to customize how each notification item is rendered.
   *  Receives the notification and a default onMarkRead handler.
   *  When provided, the caller controls the item's inner content. */
  renderItem?: (notification: Notification, onMarkRead: (id: string) => void) => React.ReactNode;
  /** Label for the mark-all-as-read button. Defaults to "Mark all as read". */
  markAllLabel?: string;
}

export function NotificationBell({ notifications, onMarkRead, onMarkAllRead, error, renderItem, markAllLabel = 'Mark all as read' }: NotificationBellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const unreadCount = notifications.filter(n => !n.read).length;

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        aria-label={`Notifications${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
        onClick={() => setIsOpen(prev => !prev)}
        className="relative p-2 rounded-full hover:bg-gray-100 transition-colors"
      >
        <Bell size={20} aria-hidden="true" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-brand-crimson text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-2xl border border-brand-steel/50 z-50"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-brand-steel/30">
            <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={onMarkAllRead}
                className="text-xs font-medium text-brand-crimson hover:text-brand-crimson/80 transition-colors"
              >
                {markAllLabel}
              </button>
            )}
          </div>

          {error ? (
            <div role="alert" aria-live="polite" className="px-4 py-3 text-sm text-brand-error">
              {error}
            </div>
          ) : (
            <ul className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <li className="px-4 py-8 text-sm text-brand-steel text-center">No notifications</li>
              ) : (
                notifications.map(notification => (
                  <li
                    key={notification.id}
                    role="menuitem"
                    className={`border-b border-brand-steel/30 last:border-0 ${renderItem ? '' : 'cursor-pointer hover:bg-brand-panel/50 transition-colors'}`}
                    onClick={renderItem ? undefined : () => onMarkRead(notification.id)}
                  >
                    {renderItem ? (
                      renderItem(notification, onMarkRead)
                    ) : (
                      <div className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-900">{notification.title}</p>
                        <p className="text-xs text-brand-steel mt-0.5">{notification.message}</p>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
